import type { PriorityExecutor } from '@lc/librunner'
import type { Action } from '@reduxjs/toolkit'
import { getLoadingState, setLoadingState, type LoadingState } from '../loading'
import type { State, Store } from '../store'

export type LoadAPI = {
	key: string
	signal: AbortSignal
	store: Store
}

export function onLoadStateChange(
	store: Store,
	id: string,
	handler: (state: LoadingState) => void,
): () => void {
	let lastState: LoadingState | null = null
	return store.subscribe(() => {
		const loadingState = getLoadingState(store.getState(), id)
		if (loadingState && loadingState !== lastState) {
			lastState = loadingState
			handler(loadingState)
		}
	})
}

export async function awaitLoaded(store: Store, id: string): Promise<void> {
	const state = getLoadingState(store.getState(), id)
	if (state?.status === 'loaded') return
	if (state?.status === 'failed') throw new Error(state.error)
	return new Promise((ok, ko) => {
		const unsub = onLoadStateChange(store, id, state => {
			if (state.status === 'loaded') {
				unsub()
				ok()
			} else if (state.status === 'failed') {
				unsub()
				ko(new Error(state.error))
			}
		})
	})
}

export async function awaitLoadComplete(
	store: Store,
	id: string,
): Promise<void> {
	try {
		await awaitLoaded(store, id)
	} catch {
		return
	}
}

export type LoaderImpl = (
	api: LoadAPI,
) => Promise<Action | Action[] | null | void>

export function load({
	store,
	executor,
	id,
	name = `load(${JSON.stringify(id)})`,
	level,
	loader,
	check,
}: {
	store: Store
	executor: PriorityExecutor
	id: string
	name?: string
	level: number
	loader: LoaderImpl
	check?: (state: State) => boolean
}): Promise<void> & { id: string; status: 'pending' | 'loading' | 'loaded' } {
	if (process.env.SSR) {
		if (level >= 2) {
			return Object.assign(Promise.resolve(), {
				id,
				status: 'pending',
			} as const)
		}
		return Object.assign(loadImplSsr({ store, id, loader, check }), {
			id,
			status: 'pending',
		} as const)
	} else {
		const status = loadImpl({
			store,
			executor,
			id,
			name,
			level,
			loader,
			check,
		})
		return Object.assign(awaitLoadComplete(store, id), {
			id,
			status,
		} as const)
	}
}

export interface Loader<T> {
	(
		params: { store: Store; executor: PriorityExecutor; level: number } & T,
	): Promise<void>
	id(params: T): string
}

export function makeLoader<T>({
	id,
	loader,
	check,
}: {
	id: ((params: T) => string) | string
	loader: (
		api: LoadAPI,
		params: T,
	) => Promise<Action | Action[] | null | void>
	check?: (state: State, params: T) => boolean
}): Loader<T> {
	if (typeof id === 'string') {
		const realId = id
		id = () => realId
	}
	const built = ({
		store,
		executor,
		level,
		...params
	}: {
		store: Store
		executor: PriorityExecutor
		level: number
	} & T) =>
		load({
			store,
			executor,
			id: id(params as T),
			level,
			loader: api => loader(api, params as T),
			check: check ? state => check(state, params as T) : undefined,
		})
	return Object.assign(built, { id })
}

async function loadImplSsr({
	store,
	id,
	loader,
	check,
}: {
	store: Store
	id: string
	loader: (api: LoadAPI) => Promise<Action | Action[] | null | void>
	check?: (state: State) => boolean
}): Promise<void> {
	try {
		const state = store.getState()
		const loadingState = getLoadingState(state, id)
		const status = loadingState?.status ?? 'none'
		if (check?.(state)) {
			if (status !== 'loaded')
				store.dispatch(setLoadingState([id, { status: 'loaded' }]))
			return
		} else if (status === 'loaded') {
			return
		}
	} catch {
		// ignore
	}

	const abortController = new AbortController()
	const api: LoadAPI = {
		key: id,
		signal: abortController.signal,
		store,
	}
	try {
		const toDispatch = await loader(api)
		if (Array.isArray(toDispatch)) {
			for (const action of toDispatch) store.dispatch(action)
		} else if (toDispatch) {
			store.dispatch(toDispatch)
		}
		store.dispatch(setLoadingState([id, { status: 'loaded' }]))
		return
	} catch (e) {
		console.error(e)
		const message = e instanceof Error ? e.message : 'unknown error'
		store.dispatch(
			setLoadingState([id, { status: 'failed', error: message }]),
		)
		return
	}
}

function loadImpl({
	store,
	executor,
	id,
	name,
	level,
	loader,
	check,
}: {
	store: Store
	executor: PriorityExecutor
	id: string
	name: string
	level: number
	loader: (api: LoadAPI) => Promise<Action | Action[] | null | void>
	check?: (state: State) => boolean
}) {
	const state = store.getState()
	const loadingState = getLoadingState(state, id)
	const status = loadingState?.status ?? 'none'

	// if we're already done, just return
	if (check?.(state)) {
		if (status !== 'loaded')
			store.dispatch(setLoadingState([id, { status: 'loaded' }]))
		return 'loaded'
	}

	// if the task is already pending
	if (loadingState?.status === 'pending') {
		if (loadingState.level > level) {
			// if it's pending at a level higher than requested, try and reschedule it to our level
			const cancelled = executor.cancel({
				id: loadingState.key,
				level: loadingState.level,
			})
			if (!cancelled) {
				// we couldn't reschedule it, meaning it's already in the process of getting ran
				return 'pending'
			}
		} else {
			// if it's pending at our level or lower, nothing to do
			return 'pending'
		}
	}

	// if the task is already loaded or loading, just return
	if (status === 'loaded' || status === 'loading') return status

	// schedule the task
	const abortController = new AbortController()
	const task = executor.schedule({ level, name }, async () => {
		// this runs at least on the next microtask

		// the task got scheduled to run, so update its status
		store.dispatch(
			setLoadingState([id, { status: 'loading', key: task.id }]),
		)

		try {
			const api: LoadAPI = {
				key: task.id,
				signal: abortController.signal,
				store,
			}
			const toDispatch = await loader(api)

			if (!toDispatch) {
				// nothing to do
			} else if (Array.isArray(toDispatch)) {
				for (const action of toDispatch) store.dispatch(action)
			} else {
				store.dispatch(toDispatch)
			}

			store.dispatch(setLoadingState([id, { status: 'loaded' }]))
		} catch (e) {
			const message = e instanceof Error ? e.message : 'unknown error'
			store.dispatch(
				setLoadingState([id, { status: 'failed', error: message }]),
			)
		}
	})

	// add the task to the store
	store.dispatch(
		setLoadingState([id, { status: 'pending', key: task.id, level }]),
	)
	return 'pending'
}
